import { type ComponentProps, useEffect } from 'react'
import { motion } from 'motion/react'
import { nanoid } from 'nanoid'
import { toast as sonnerToast, Toaster as SonnerToaster } from 'sonner'
import { t } from '@lingui/core/macro'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import {
  removeBackgroundJobsByActionId,
  useBackgroundJobActions,
  useBackgroundJobStore,
} from '@jotta/hooks'
import SvgX from '@jotta/ui/SvgX'

import Spinner from '../Elements/ProgressIndicator/Circular/Spinner/Spinner'
import { PlainBrandIcon } from '../Icons/BrandIcon/BrandIcon'

import { getToastContent } from './backgroundJobToasts'

export type ToastStatus = 'IN_PROGRESS' | 'SUCCESS' | 'FAILED'

type ToastOptions = {
  id?: string | number
  duration?: number | 'short'
  onDismiss?: () => void
  actionButton?: {
    label: string
    onClick: () => void
  }
}

export type ToastContent = {
  minHeight?: number // this is used as a workaround for state changes in headless sonner toasts that needs to change in size. A github issue has been created for this: https://github.com/emilkowalski/sonner/issues/616
  message: string
  isLoading?: boolean
  hasCloseButton?: boolean
  testid?: string
}

export const dismissToast = (id: string | number) => {
  sonnerToast.dismiss(id)
}

const useBackgroundJobToasts = () => {
  const authenticated = useAuthenticated()
  const actions = useBackgroundJobActions()
  const backgroundJobs = useBackgroundJobStore(state => state.backgroundJobs)
  const removeBackgroundJobs = useBackgroundJobStore(
    state => state.removeBackgroundJobs,
  )

  useEffect(() => {
    if (authenticated === 'FALSE') {
      actions.forEach(({ actionId }) => dismissToast(actionId))
      const jobIds = Object.keys(backgroundJobs)
      if (jobIds.length > 0) {
        removeBackgroundJobs(jobIds)
      }
      return
    }

    if (authenticated !== 'TRUE') {
      return
    }

    actions.forEach(({ actionId, jobType, status }) => {
      showToast({
        ...getToastContent(jobType, status),
        id: actionId,
        onDismiss: () => removeBackgroundJobsByActionId(actionId),
      })
    })
  }, [actions, authenticated, backgroundJobs, removeBackgroundJobs])
}

export const ToastProvider = ({
  ...props
}: ComponentProps<typeof SonnerToaster>) => {
  useBackgroundJobToasts()
  return <SonnerToaster {...props} />
}

const Toast = ({
  id,
  toastId,
  message,
  minHeight,
  isLoading,
  onCloseButtonClick,
  actionButton,
  testid,
}: ToastContent &
  ToastOptions & {
    toastId: string | number
    onCloseButtonClick?: () => void
  }) => (
  <motion.div
    id={id?.toString()}
    data-testid={testid}
    style={{ minHeight: minHeight || 40 }}
    className="pointer-events-auto flex w-full min-w-[258px] items-center justify-between gap-2 self-center rounded-[4px] border border-grey4 bg-grey1-fg px-4 py-1 text-white shadow-md"
    layout
    transition={{
      delay: 0.1,
      layout: {
        type: 'spring',
        duration: 0.15,
        ease: 'easeInOut',
      },
    }}
  >
    <p className="body-sm-ln">{message}</p>
    <div className="flex items-center gap-2">
      {actionButton?.label && actionButton?.onClick && (
        <button
          className="btn btn-ghost text-white"
          onClick={() => {
            actionButton.onClick()
            dismissToast(toastId)
          }}
        >
          {actionButton.label}
        </button>
      )}
      {isLoading && <Spinner className="stroke-white" size="small" />}
      {onCloseButtonClick && (
        <button
          className="btn btn-ghost size-5 p-0 hover:bg-transparent"
          onClick={onCloseButtonClick}
          aria-label={t`Close`}
        >
          <PlainBrandIcon icon={SvgX} className="size-5 text-white" />
        </button>
      )}
    </div>
  </motion.div>
)

export function showToast(toast: ToastContent & ToastOptions) {
  const id = toast.id ?? nanoid()
  const duration =
    toast.duration === 'short'
      ? 2000
      : (toast.duration ?? (toast.isLoading ? Infinity : 20000))
  const handleActionButtonClick = () => {
    toast?.actionButton?.onClick()
  }
  const handleClose = () => {
    toast?.onDismiss?.()
    dismissToast(id)
  }

  return sonnerToast.custom(
    (toastId: string | number) => (
      <Toast
        toastId={toastId}
        key={toastId}
        id={toastId}
        message={toast.message}
        minHeight={toast.minHeight}
        isLoading={toast.isLoading}
        testid={toast.testid}
        onCloseButtonClick={toast.hasCloseButton ? handleClose : undefined}
        actionButton={
          toast.actionButton
            ? {
                label: toast.actionButton.label,
                onClick: handleActionButtonClick,
              }
            : undefined
        }
      />
    ),
    {
      duration,
      position: 'bottom-center',
      id: id,
      onDismiss: handleClose,
    },
  )
}
